import { useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import Select from 'react-select';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Settings } from 'react-feather';
import { useFloating, useInteractions, useClick, useDismiss } from '@floating-ui/react';
import { useContext, useMemo, useState } from 'react';
import { useTheme } from '@trinity/chat-components/components/Theme';
import * as Presentation from '../Chat.Filters/Presentation';
import { ChartGroupByContext, ChartInfoContext, ChartTypeContext, ChartRecordIdContext } from './context';
import { barToPie, pieToBar } from './Message.Response.ChartContainer';
import { ChartFilterButton } from './Chat.Chart.Filter.Button';
import { ChartFilterMenu } from './Chat.Chart.Filter.Menu';
import { useChartFilters, type FilterRow } from './Chat.Chart.Filter.Context';
import { useActiveChartFiltersCount } from './Chat.Chart.Filter.utils';
import { Bar, BarChart, Line, LineChart, MultipleBarChart, MultipleLineChart, PieChart } from '@trinity/chat';
import Input from '../Input';
import { ChartType, mapChartTypeToBackend } from './Chat.Chart.utils';
import { UpdateChartPreferencesContext } from '@trinity/chat-components/containers/ChartPreferences/context';

// Data conversion function to transform various chart data types to ChartDataPoint[]
function convertToChartDataPoints(
  data: { id: string; label: string; value: number }[] |
    { x: string; y: number }[] |
    Bar[] |
    Line |
    Line[]
): { id?: string; label?: string; x?: string; value?: number; y?: number }[] {
  if (!data) return [];

  // Handle PieChart data: { id: string; label: string; value: number }[]
  if (Array.isArray(data) && data.length > 0 && 'id' in data[0] && 'label' in data[0]) {
    return data as { id: string; label: string; value: number }[];
  }

  // Handle BarChart data: { x: string; y: number }[]
  if (Array.isArray(data) && data.length > 0 && 'x' in data[0] && 'y' in data[0]) {
    return data as { x: string; y: number }[];
  }

  // Handle MultipleBarChart data: Chat.Bar[]
  if (Array.isArray(data) && data.length > 0 && 'index' in data[0] && 'data' in data[0]) {
    const bars = data as Bar[];
    return bars.flatMap(bar =>
      bar.data.map(point => ({
        x: point.x,
        y: point.y,
        label: bar.index, // Use bar index as label for grouping
      }))
    );
  }

  // Handle LineChart data: Chat.Line
  if (!Array.isArray(data) && data && 'id' in data && 'data' in data) {
    const line = data;
    return line.data.map(point => ({
      x: typeof point.x === 'string' ? point.x : point.x.toString(),
      y: point.y,
      label: line.id,
    }));
  }

  // Handle MultipleLineChart data: Chat.Line[]
  if (Array.isArray(data) && data.length > 0 && 'id' in data[0] && 'data' in data[0]) {
    const lines = data as Line[];
    return lines.flatMap(line =>
      line.data.map(point => ({
        x: typeof point.x === 'string' ? point.x : point.x.toString(),
        y: point.y,
        label: line.id,
      }))
    );
  }

  return [];
}

export function ChartFilterPane() {
  const [isOpen, setIsOpen] = useState(false);
  const [chart, setChart] = useContext(ChartInfoContext);
  const [selectedChartType, setSelectedChartType] = useContext(ChartTypeContext);
  const [filterOpen, setFilterOpen] = useState(false);

  // Supported chart types for V1 filter panel
  const SUPPORTED_FILTER_CHART_TYPES_V1 = ['pie', 'donut', 'bar', 'stacked-bar'];

  const isFilterSupported = SUPPORTED_FILTER_CHART_TYPES_V1.includes(selectedChartType);

  // FILTER MENU FLOATING LOGIC
  const {
    refs: filterRefs,
    floatingStyles: filterFloatingStyles,
    context: filterContext,
  } = useFloating({
    open: filterOpen,
    onOpenChange: setFilterOpen,
    placement: 'bottom-start',
  });

  const filterClick = useClick(filterContext);
  const filterDismiss = useDismiss(filterContext);
  const { getFloatingProps: getFilterFloatingProps, getReferenceProps: getFilterReferenceProps } = useInteractions([filterClick, filterDismiss]);

  // SETTINGS MENU FLOATING LOGIC
  const { context, refs, floatingStyles } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const originalDataRef = useRef<
    { id: string; label: string; value: number }[] | // PieChart
    { x: string; y: number }[] |                    // BarPoints (BarChart)
    Bar[] |                                    // MultipleBarChart
    Line |                                     // LineChart
    Line[]                                     // MultipleLineChart
  >(chart.chart.chart.data);

  const chartType: string = chart?.chart?.type || 'unknown';
  const chartId = `${chart.quantItemId}_${chartType}`;

  // Get the active filters count for this chart
  const activeFiltersCount = useActiveChartFiltersCount(chartId);

  // Get filters from context (now properly typed)
  const chartFiltersMap = useChartFilters();
  const filters = chartFiltersMap[chartId] ?? [];

  useEffect(() => {
    if (filters && filters.length > 0) {
      handleFiltersChange(filters); // This updates the chart data
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, chartId, chartType]);

  const handleFiltersChange = (filters: FilterRow[]) => {
    const originalData = originalDataRef.current;
    const chartTypeLocal: string = chart.chart.type;

    const hasAnySelection = filters.some(
      filterRow => Array.isArray(filterRow.valueOption) && filterRow.valueOption.length > 0
    );
    if (!hasAnySelection) {
      setChart(prev => ({
        ...prev,
        chart: {
          ...prev.chart,
          chart: {
            ...prev.chart.chart,
            data: originalData,
          },
        },
      }) as typeof chart);
      return;
    }

    let filteredData:
      | { id: string; label: string; value: number }[] // PieChart
      | { x: string; y: number }[]                    // BarPoints (BarChart)
      | Bar[]                                    // MultipleBarChart
      | Line                                     // LineChart
      | Line[]                                   // MultipleLineChart
      = originalData;

    if (chartTypeLocal === 'pie') {
      filteredData = (originalData as { id: string; label: string; value: number }[]).filter(
        dataPoint =>
          typeof dataPoint === 'object' &&
          dataPoint !== null &&
          typeof (dataPoint as { label?: unknown }).label === 'string' &&
          typeof (dataPoint as { id?: unknown }).id === 'string' &&
          typeof (dataPoint as { value?: unknown }).value === 'number'
      );

      // Gather all selected and not_selected labels
      const selectedLabels = filters
        .filter(filterRow => filterRow.selectedOption?.value === 'selected')
        .flatMap(filterRow => (filterRow.valueOption ?? []).map(option => option.value));
      const notSelectedLabels = filters
        .filter(filterRow => filterRow.selectedOption?.value === 'not_selected')
        .flatMap(filterRow => (filterRow.valueOption ?? []).map(option => option.value));

      if (selectedLabels.length > 0) {
        filteredData = filteredData.filter(
          dataPoint =>
            typeof dataPoint === 'object' &&
            dataPoint !== null &&
            selectedLabels.includes((dataPoint as { label: string }).label)
        );
      }
      if (notSelectedLabels.length > 0) {
        filteredData = filteredData.filter(
          dataPoint =>
            typeof dataPoint === 'object' &&
            dataPoint !== null &&
            !notSelectedLabels.includes((dataPoint as { label: string }).label)
        );
      }
    } else if (chartTypeLocal === 'bar' || chartTypeLocal === 'stacked-bar') {
      filteredData = (originalData as { x: string; y: number }[]).filter(
        dataPoint =>
          typeof dataPoint === 'object' &&
          dataPoint !== null &&
          typeof (dataPoint as { x?: unknown }).x === 'string' &&
          typeof (dataPoint as { y?: unknown }).y === 'number'
      );

      const selectedLabels = filters
        .filter(filterRow => filterRow.selectedOption?.value === 'selected')
        .flatMap(filterRow => (filterRow.valueOption ?? []).map(option => option.value));
      const notSelectedLabels = filters
        .filter(filterRow => filterRow.selectedOption?.value === 'not_selected')
        .flatMap(filterRow => (filterRow.valueOption ?? []).map(option => option.value));

      if (selectedLabels.length > 0) {
        filteredData = filteredData.filter(
          dataPoint =>
            typeof dataPoint === 'object' &&
            dataPoint !== null &&
            selectedLabels.includes((dataPoint as { x: string }).x)
        );
      }
      if (notSelectedLabels.length > 0) {
        filteredData = filteredData.filter(
          dataPoint =>
            typeof dataPoint === 'object' &&
            dataPoint !== null &&
            !notSelectedLabels.includes((dataPoint as { x: string }).x)
        );
      }
    }

    if (['pie', 'donut', 'bar', 'stacked-bar'].includes(chartTypeLocal)) {
      setChart(prev => {
        switch (prev.chart.type) {
          case 'multiple-stacked-bar':
          case 'multiple-clustered-bar':
            return {
              ...prev,
              chart: {
                ...prev.chart,
                chart: {
                  ...(prev.chart.chart as MultipleBarChart),
                  data: filteredData as Bar[],
                  indexBy: (prev.chart.chart as MultipleBarChart).indexBy,
                },
              },
            };
          case 'bar':
          case 'stacked-bar':
            return {
              ...prev,
              chart: {
                ...prev.chart,
                chart: {
                  ...(prev.chart.chart as BarChart),
                  data: filteredData as { x: string; y: number }[],
                },
              },
            };
          case 'pie':
            return {
              ...prev,
              chart: {
                ...prev.chart,
                chart: {
                  ...(prev.chart.chart as PieChart),
                  data: filteredData as { id: string; label: string; value: number }[],
                },
              },
            };
          case 'line':
            return {
              ...prev,
              chart: {
                ...prev.chart,
                chart: {
                  ...(prev.chart.chart as LineChart),
                  data: filteredData as Line[],
                },
              },
            };
          case 'multiple-line':
            return {
              ...prev,
              chart: {
                ...prev.chart,
                chart: {
                  ...(prev.chart.chart as MultipleLineChart),
                  data: filteredData as Line[],
                },
              },
            };
          default:
            return prev;
        }
      });
    }
  };

  const { getFloatingProps, getReferenceProps } = useInteractions([click, dismiss]);

  return (
    <TopRow>
      {/* FILTER BUTTON - Always show but conditionally disable */}
      <span
        ref={filterRefs.setReference}
        {...getFilterReferenceProps()}
        style={{ display: 'flex', alignItems: 'center' }}>
        <ChartFilterButton
          onClick={() => setFilterOpen(open => !open)}
          disabled={!isFilterSupported}
          filtersCount={activeFiltersCount} />
      </span>

      {/* FILTER MENU - Only show when supported and open */}
      {filterOpen && isFilterSupported && (
        <PopperRoot
          ref={filterRefs.setFloating}
          style={filterFloatingStyles}
          {...getFilterFloatingProps()}>
          <PopperHeader>Chart Filters</PopperHeader>
          <PopperContent>
            <ChartFilterMenu
              chartInfo={{ ...chart, filters }}
              originalData={convertToChartDataPoints(originalDataRef.current)} />
          </PopperContent>
        </PopperRoot>
      )}

      {/* SETTINGS BUTTON */}
      <span
        ref={refs.setReference}
        {...getReferenceProps()}
        style={{ display: 'flex', alignItems: 'center' }}>
        <StyledSettingsIcon />
      </span>

      {/* SETTINGS MENU */}
      {isOpen && (
        <Root
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}>
          <Header>
            <HeaderLabel>Chart Settings</HeaderLabel>
          </Header>
          <Filters>
            <ChartTypeController
              selectedChartType={selectedChartType}
              onChange={setSelectedChartType} />

            <LabelContainer>
              <Label>Group small values into &quot;Others&quot;</Label>
              <SubLabel> Only applies when chart data contains small categories </SubLabel>
            </LabelContainer>

            <GroupController
              disabled={selectedChartType !== 'stacked-bar' && selectedChartType !== 'bar' && selectedChartType !== 'pie'} />

            {(selectedChartType !== 'stacked-bar' && selectedChartType !== 'bar' && selectedChartType !== 'pie') && (
              <ErrorContainer>
                <StyledErrorIcon />
                <ErrorMessage> &quot;Others grouping&quot; is not available for this chart type or data set.</ErrorMessage>
              </ErrorContainer>
            )}
          </Filters>
        </Root>
      )}
    </TopRow>
  );
}

const TopRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

function ChartTypeController({ selectedChartType, onChange }: { selectedChartType: string | null; onChange: (value: string | null) => void }) {
  const [_, setChart] = useContext(ChartInfoContext);
  const chartRecordId = useContext(ChartRecordIdContext);
  const overrideTypesRef = useRef<string[]>([]);
  const theme = useTheme();

  const updateChartRecordOverride = useContext(UpdateChartPreferencesContext);

  const selectStyles = useMemo(() => Presentation.buildSelectStyles(theme), [theme]);
  const selectTheme = useMemo(() => Presentation.buildSelectTheme(theme), [theme]);

  const filteredOptions = useMemo(() => {

    const singleOptions = [
      { value: 'bar', label: 'Bar' },
      { value: 'pie', label: 'Pie' },
      { value: 'stacked-bar', label: 'Singular Stacked Bar' },
    ];

    const multipleOptions = [
      { value: 'multiple-stacked-bar', label: 'Multiple Stacked Bar' },
      { value: 'multiple-clustered-bar', label: 'Multiple Clustered Bar' },
    ];

    if (singleOptions.map(option => option.value).includes(selectedChartType)) {
      return singleOptions;
    }
    else {
      return multipleOptions;
    }
  }, [selectedChartType]);
  const handleChange = (option: { value: string }) => {
    const newChartType = option.value;

    // Record the chart type override
    const recordChartOverride = async () => {
      if (chartRecordId && selectedChartType !== newChartType) {
        try {
          const mappedChartType = mapChartTypeToBackend(newChartType);
          if (mappedChartType) {
            overrideTypesRef.current = [mappedChartType];
            await updateChartRecordOverride({
              recordId: chartRecordId,
              overrideChartTypes: overrideTypesRef.current as ChartType[],
            });
          }
        } catch (error) {
          console.warn('Failed to record chart type override:', error);
        }
      }
    };

    if ((selectedChartType === 'bar' || selectedChartType === 'stacked-bar') && newChartType === 'pie') {
      setChart(prev => ({
        ...prev,
        chart: {
          ...prev.chart,
          type: newChartType,
          chart: barToPie(prev.chart.chart as BarChart),
        },
      }));
    }

    else if (selectedChartType === 'pie' && (newChartType === 'bar' || newChartType === 'stacked-bar')) {
      setChart(prev => ({
        ...prev,
        chart: {
          ...prev.chart,
          type: newChartType,
          chart: pieToBar(prev.chart.chart as PieChart),
        },
      }));
    }

    else if (selectedChartType === 'multiple-stacked-bar' && newChartType === 'multiple-clustered-bar') {
      setChart(prev => ({
        ...prev,
        chart: {
          ...prev.chart,
          type: newChartType,
          chart: prev.chart.chart,
        },
      }));
    }

    else if (selectedChartType === 'multiple-clustered-bar' && newChartType === 'multiple-stacked-bar') {
      setChart(prev => ({
        ...prev,
        chart: {
          ...prev.chart,
          type: newChartType,
          chart: prev.chart.chart,
        },
      }));
    }

    onChange(newChartType);
    recordChartOverride();
  };

  return (
    <ChartTypeContainer>
      <Label>Chart Type</Label>
      <Select
        options={filteredOptions}
        id="chart-type"
        value={filteredOptions.find(option => option.value === selectedChartType)}
        onChange={handleChange}
        styles={selectStyles}
        theme={selectTheme} />
    </ChartTypeContainer>
  );
}

function GroupController({ disabled }: { disabled: boolean }) {
  const [groupBy, setGroupBy] = useContext(ChartGroupByContext);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setGroupBy(+value);
  };

  return (
    <GroupContainer>
      <Label>Show Top N Categories:</Label>
      <Input
        id="group-values-below"
        placeholder="Input Number"
        type="number"
        value={groupBy || ''}
        disabled={disabled}
        onChange={handleInputChange} />
    </GroupContainer>
  );
}

const PopperRoot = styled.div(({ theme }) => ({
  background: theme?.palette?.white?.main || '#fff',
  border: `1px solid ${theme?.palette?.gray?.light1 || '#e0e0e0'}`,
  borderRadius: 6,
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  minWidth: 280,
  zIndex: 1000,
  marginTop: 4,
}));

const PopperHeader = styled.div(({ theme }) => ({
  fontWeight: 600,
  fontSize: 16,
  padding: '12px 16px 8px 16px',
  borderBottom: `1px solid ${theme?.palette?.gray?.light2 || '#f0f0f0'}`,
}));

const PopperContent = styled.div({
  padding: '12px 16px',
});

const Root = styled.div(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  borderRadius: '5px',
  border: `1px solid ${theme.palette.gray.light1}`,
  boxShadow: '0 2px 6px 2px rgba(60, 64, 67, .15)',
  boxSizing: 'border-box',
  padding: '12px',
  minWidth: 280,
  zIndex: 1000,
}));

const Header = styled.div({
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '10px',
});

const HeaderLabel = styled.h1(({ theme }) => ({
  fontSize: '18px',
  fontFamily: theme.fonts.semiBold,
}));

const Filters = styled.div({
  boxSizing: 'border-box',
  padding: '0 2px',
});

const StyledErrorIcon = styled(ErrorOutlineIcon)({
  color: 'red',
  fontSize: '12px',
  marginLeft: '5px',
});

const ErrorMessage = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.regular,
  color: 'red',
  fontSize: '10px',
}));

const ErrorContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
});

const ChartTypeContainer = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '5px',
  marginBottom: '10px',
});

const GroupContainer = styled.div({
  display: 'flex',
  whiteSpace: 'nowrap',
  alignItems: 'center',
  gap: '2px',
  marginBottom: '10px',
});

const Label = styled.label(({ theme }) => ({
  whiteSpace: 'nowrap',
  fontFamily: theme.fonts.semiBold,
  fontSize: '14px',
}));

const SubLabel = styled.label(({ theme }) => ({
  fontFamily: theme.fonts.regular,
  fontSize: '10px',
  color: theme.palette.gray.light1,
}));

const LabelContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  marginBottom: '10px',
});

const StyledSettingsIcon = styled(Settings)(({ theme }) => ({
  fontSize: '12px !important',
  cursor: 'pointer',
  color: 'var(--gray-d)',
}));